import React, { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ErrorMessage } from 'secondstep-components'
import { VIEW_OPTION_PATHS } from 'utils/adminExperienceHelpers'
import {
  TOGGLE_VIEW,
  isK8ProgressToggleView,
} from 'store/userSessionManager/constants'
import AdminDashboardBanner from 'components/AdminDashboardBanner'
import AdminHSMonitorView from 'components/AdminHSMonitorView'
import AdminHSMultiSiteView from 'components/AdminHSMultiSiteView'
import AdminHSSingleSiteView from 'components/AdminHSSingleSiteView'
import AdminK8PrepareMultiSiteView from 'components/AdminK8PrepareMultiSiteView'
import AdminK8Tier2View from 'components/AdminK8Tier2View'
import AdminMultiSiteView from 'components/AdminMultiSiteView'
import AdminSchoolTable from 'components/AdminSchoolTable'
import AdminSingleSiteView from 'components/AdminSingleSiteView'
import AdminSitesSummary from 'components/AdminSitesSummary'
import GetStartedPanel from 'components/admin/GetStartedPanel'
import {
  ADMIN_DASHBOARD_SUB_ROUTES,
  AVAILABLE_PROGRAMS,
  PROGRAM_TITLES,
} from './constants'
import { AdminDashboardWrapper, ContentWrapper } from './component.styles'

const AdminDashboard = ({
  activeAdminK8Sites,
  adminK8SitesWithDwb = [],
  adminHsSitesWithDwb = [],
  adminHSSites,
  match,
  navigationEvent,
  onboardingSnapshot,
  programTitles,
  selectedProgram: storedSelectedProgram,
  setLeaderDashboardRecentUrl,
  hsToggleView,
  k8ToggleView,
  updateSelectedProgram,
}) => {
  const [selectedProgram, setSelectedProgram] = useState()

  const tabTitles = {
    page: 'Leader Dashboard',
    tab: selectedProgram?.name,
  }

  useEffect(() => {
    setSelectedProgram({
      name: PROGRAM_TITLES[storedSelectedProgram?.name],
      key: storedSelectedProgram?.name,
    })
  }, [storedSelectedProgram])

  useEffect(() => {
    if (selectedProgram) {
      document.title = `${tabTitles.page} | ${tabTitles.tab}`
    }
  }, [selectedProgram])

  const createProgramRoute = program => {
    const hsViewOptionSegment =
      hsToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
        ? VIEW_OPTION_PATHS.SPECIALIZED_UNITS
        : hsToggleView === TOGGLE_VIEW.MONITOR
        ? VIEW_OPTION_PATHS.MONITOR
        : hsToggleView === TOGGLE_VIEW.PREPARE
        ? VIEW_OPTION_PATHS.PREPARE
        : VIEW_OPTION_PATHS.PROGRAM_ADOPTION
    if (program.key === 'HS') {
      return `${program.key.toLowerCase()}/${hsViewOptionSegment}`
    }

    const k8ViewOptionSegment =
      k8ToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
        ? VIEW_OPTION_PATHS.SPECIALIZED_UNITS
        : isK8ProgressToggleView(k8ToggleView)
        ? VIEW_OPTION_PATHS.CORE
        : k8ToggleView === TOGGLE_VIEW.SETUP
        ? VIEW_OPTION_PATHS.SETUP
        : VIEW_OPTION_PATHS.LAUNCH

    if (program.key === 'K8') {
      return `${program.key.toLowerCase()}/${k8ViewOptionSegment}`
    } else if (program.key === 'HS') {
      return `${program.key.toLowerCase()}/${hsViewOptionSegment}`
    } else {
      return program.key.toLowerCase()
    }
  }

  const onChangeProgram = program => {
    setSelectedProgram(program)
    updateSelectedProgram({ name: program.key, key: program.key })

    const programRoute = createProgramRoute(program)
    const route = `program/${programRoute}`
    setLeaderDashboardRecentUrl(route)
    navigationEvent(`/admin/${route}`)
  }

  const { params } = match
  const { programName = '', viewOption = null } = params
  const validProgram = AVAILABLE_PROGRAMS.find(
    program => program === programName.toUpperCase(),
  )
  const isValidSubRoute =
    ADMIN_DASHBOARD_SUB_ROUTES[programName.toLowerCase()] &&
    ADMIN_DASHBOARD_SUB_ROUTES[programName.toLowerCase()].includes(viewOption)

  if (!validProgram || (viewOption && !isValidSubRoute)) {
    return <ErrorMessage statusCode={404} />
  }

  let isLaunched = false
  let isSetup = false

  if (selectedProgram?.key === 'K8') {
    isLaunched = true
  }

  if (selectedProgram?.key === 'HS') {
    if (Array.isArray(adminHSSites)) {
      isLaunched = adminHSSites?.find(
        site => site.sitePreferences?.highSchool?.isLaunched === true,
      )
      isSetup = adminHSSites?.find(
        site => site.sitePreferences?.highSchool?.isSetup === true,
      )
    }
  }

  const isSingleHsSite = adminHSSites?.length === 1

  const isHsSpecializedProgressView =
    hsToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
  const hsProgressMultiSiteCount = isHsSpecializedProgressView
    ? adminHsSitesWithDwb?.length
    : 0
  const hsProgressIsMultiSite = hsProgressMultiSiteCount > 1
  const hsProgressIsSingleSite = hsProgressMultiSiteCount === 1

  const isK8SpecializedProgressView =
    k8ToggleView === TOGGLE_VIEW.SPECIALIZED_UNITS
  const k8ProgressMultiSiteCount = isK8SpecializedProgressView
    ? adminK8SitesWithDwb?.length
    : activeAdminK8Sites?.length
  const k8ProgressIsMultiSite = k8ProgressMultiSiteCount > 1
  const k8ProgressIsSingleSite = k8ProgressMultiSiteCount === 1

  return (
    <AdminDashboardWrapper>
      <Fragment key="admin-banner">
        <AdminDashboardBanner
          hsToggleView={hsToggleView}
          isLaunched={!!isLaunched}
          isSetup={!!isSetup}
          k8ToggleView={k8ToggleView}
          navigationEvent={navigationEvent}
          onChangeProgram={onChangeProgram}
          onboardingSnapshot={onboardingSnapshot}
          programTitles={programTitles}
        />
        <ContentWrapper>
          {selectedProgram?.key === 'HS' && (
            <Fragment key="admin-hs-view">
              {hsToggleView === TOGGLE_VIEW.PREPARE && (
                <Fragment key="admin-hs-prepare-view">
                  {adminHSSites?.length > 1 && <AdminK8PrepareMultiSiteView />}
                </Fragment>
              )}
              {hsToggleView === TOGGLE_VIEW.LAUNCH && (
                <Fragment key="admin-hs-launch-view">
                  {isSingleHsSite && (
                    <Fragment key="admin-hs-launch-view-single-site">
                      {!isLaunched && (
                        <GetStartedPanel programKey={selectedProgram.key} />
                      )}
                      <AdminHSSingleSiteView
                        onboardingSnapshot={onboardingSnapshot}
                        programKey={selectedProgram.key}
                      />
                    </Fragment>
                  )}
                  {adminHSSites?.length > 1 && <AdminHSMultiSiteView />}
                </Fragment>
              )}
              {hsToggleView === TOGGLE_VIEW.MONITOR && (
                <Fragment key="admin-hs-monitor-view">
                  {isSingleHsSite && <AdminHSMonitorView />}
                  {adminHSSites?.length > 1 && <AdminHSMultiSiteView />}
                </Fragment>
              )}
              {isHsSpecializedProgressView && (
                <Fragment key="admin-hs-dwb-progress-views">
                  {hsProgressIsMultiSite && <AdminMultiSiteView />}
                  {hsProgressIsSingleSite && <AdminSingleSiteView />}
                </Fragment>
              )}
            </Fragment>
          )}
          {selectedProgram?.key === 'K8' && (
            <Fragment key="k8-content">
              {k8ToggleView === TOGGLE_VIEW.SETUP && (
                <Fragment key="k8-setup-view">
                  {activeAdminK8Sites?.length > 1 && (
                    <AdminK8PrepareMultiSiteView />
                  )}
                </Fragment>
              )}
              {k8ToggleView === TOGGLE_VIEW.LAUNCH && (
                <Fragment key="k8-launch-view">
                  <AdminSitesSummary />
                  <AdminSchoolTable
                    navigationEvent={navigationEvent}
                    selectedProgram={selectedProgram}
                  />
                </Fragment>
              )}
              {isK8ProgressToggleView(k8ToggleView) && (
                <Fragment key="k8-progress-views">
                  {k8ProgressIsMultiSite && <AdminMultiSiteView />}
                  {k8ProgressIsSingleSite && <AdminSingleSiteView />}
                </Fragment>
              )}
              {k8ToggleView === TOGGLE_VIEW.INTERVENTIONS && (
                <Fragment key="k8-tier-2-view">
                  {/* NOTE: this is a TEMPORARY component to test the Tier 2 dashboard data */}
                  {/* feel free to replace this or update it as you see fit */}
                  <AdminK8Tier2View />
                </Fragment>
              )}
            </Fragment>
          )}
        </ContentWrapper>
      </Fragment>
    </AdminDashboardWrapper>
  )
}

AdminDashboard.propTypes = {
  activeAdminK8Sites: PropTypes.array,
  adminHSSites: PropTypes.array,
  adminHsSitesWithDwb: PropTypes.array,
  adminK8SitesWithDwb: PropTypes.array,
  hsToggleView: PropTypes.string,
  k8ToggleView: PropTypes.string,
  match: PropTypes.object,
  multiSiteAdmin: PropTypes.bool,
  navigationEvent: PropTypes.func,
  onboardingSnapshot: PropTypes.array,
  programTitles: PropTypes.array,
  progressMonitoringSnapshot: PropTypes.array,
  selectedProgram: PropTypes.object,
  setLeaderDashboardRecentUrl: PropTypes.func,
  showFirstTimeSetupAdmin: PropTypes.bool,
  updateSelectedProgram: PropTypes.func,
}

export default AdminDashboard
